<template>
  <div class="w-full min-h-screen bg-slate-50 dark:bg-slate-900" :dir="isRtl === true ? 'rtl' : 'ltr'">
    <div class="px-6 sm:px-8 py-6 sm:py-8">
      <AgentEvalsListView
        v-if="mode === 'list'"
        :agents="agents"
        :evals="evals"
        :loading="loadingList"
        :errorText="errorText"
        @openEval="openEval"
        @openRun="openRun"
        @createdEval="onCreatedEval"
        @setError="setError"
      />

      <AgentEvalsEditorView
        v-else-if="mode === 'editor'"
        :evalId="selectedEvalId"
        :agents="agents"
        @back="backToList"
        @openRun="openRun"
        @refreshList="loadEvals"
        @setError="setError"
      />

      <AgentEvalsRunView
        v-else-if="mode === 'run'"
        :evalId="selectedEvalId"
        :agents="agents"
        @backToEditor="backToEditor"
        @refreshList="loadEvals"
        @setError="setError"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { globalState } from '@/globalState';
import { useI18n } from '@/i18n.js';
import AgentEvalsListView from '@/components/AgentEvals/AgentEvalsListView.vue';
import AgentEvalsEditorView from '@/components/AgentEvals/AgentEvalsEditorView.vue';
import AgentEvalsRunView from '@/components/AgentEvals/AgentEvalsRunView.vue';

const { currentLanguage } = useI18n();
const isRtl = computed(() => ['he', 'ar'].includes(currentLanguage.value) === true);
const mode = ref('list');
const evals = ref([]);
const agents = ref([]);
const loadingList = ref(true);
const selectedEvalId = ref('');
const errorText = ref('');

function getAuthToken() {
  const authToken = document.cookie.split('; ').find(row => row.startsWith('authToken=') != false)?.split('=')[1];
  const jwtToken = document.cookie.split('; ').find(row => row.startsWith('jwtToken=') != false)?.split('=')[1];
  const token = authToken !== undefined && authToken !== null && authToken !== '' ? authToken : jwtToken;
  if (token === undefined || token === null) {
    return '';
  }
  return token;
}

function getAuthHeaders() {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token !== '' && token !== undefined && token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

function setError(message) {
  errorText.value = typeof message === 'string' ? message : '';
}

function normalizeAgentList(raw) {
  const out = [];
  if (Array.isArray(raw) === true) {
    for (const a of raw) {
      const id = a !== undefined && a !== null && (a.agentId !== undefined || a.agentid !== undefined) ? String(a.agentId !== undefined ? a.agentId : a.agentid) : '';
      const name = a !== undefined && a !== null && (a.agentName !== undefined || a.agentname !== undefined) ? String(a.agentName !== undefined ? a.agentName : a.agentname) : id;
      if (id.trim().length > 0) {
        out.push({ agentId: id, agentName: name });
      }
    }
  }
  return out;
}

async function loadAgents() {
  try {
    const response = await fetch(`${globalState.apiEndpoint}/api/agents/listMinimal`, {
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    if (response.ok === true) {
      const data = await response.json();
      const rows = data !== null && data !== undefined && Array.isArray(data.agents) === true ? data.agents : (Array.isArray(data) === true ? data : []);
      agents.value = normalizeAgentList(rows);
    } else {
      agents.value = [];
    }
  } catch (e) {
    agents.value = [];
  }
}

async function loadEvals() {
  loadingList.value = true;
  try {
    const response = await fetch(`${globalState.apiEndpoint}/api/agentEvals`, {
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    if (response.ok === true) {
      const data = await response.json();
      evals.value = data !== null && data !== undefined && Array.isArray(data.evals) === true ? data.evals : [];
    } else {
      evals.value = [];
    }
  } catch (e) {
    evals.value = [];
  } finally {
    loadingList.value = false;
  }
}

function openEval(evalId) {
  selectedEvalId.value = evalId !== undefined && evalId !== null ? String(evalId) : '';
  mode.value = 'editor';
  errorText.value = '';
}

async function onCreatedEval(created) {
  await loadEvals();
  if (created !== undefined && created !== null && created.evalId !== undefined && created.evalId !== null) {
    selectedEvalId.value = String(created.evalId);
    mode.value = 'editor';
    errorText.value = '';
  }
}

function openRun(evalId) {
  selectedEvalId.value = evalId !== undefined && evalId !== null ? String(evalId) : selectedEvalId.value;
  mode.value = 'run';
  errorText.value = '';
}

function backToList() {
  mode.value = 'list';
  selectedEvalId.value = '';
  errorText.value = '';
}

function backToEditor() {
  if (selectedEvalId.value.length > 0) {
    mode.value = 'editor';
  } else {
    mode.value = 'list';
  }
  errorText.value = '';
}

onMounted(async () => {
  await loadAgents();
  await loadEvals();
});
</script>


